<template>
  <div
    class="reminders-list-container reminders-list-main reminders-list-page--glass glass-tab-page"
  >
    <BrucdcumHeader :breadcrumb="['Company', 'Reminders List']" />

    <div class="glass-tab-view glass-tab-view--flush reminders-list-glass-shell">
      <div class="glass-tab-view__content reminders-list-tab-content reminders-list-page-stack">
        <div class="reminders-list-header-block">
          <div class="reminders-list-header-row">
            <div class="reminders-list-header-row__text">
              <h2>Reminders List</h2>
              <p class="reminders-list-header-row__sub">
                Review scheduled reminders and create or skip selected transactions
              </p>
            </div>
            <div class="reminders-list-header-filter">
              <LabelDropdown
                label="Current View"
                select-id="reminders-list-current-view"
                v-model="currentView"
                inline
                :searchable="false"
                :options="currentViewFilterOptions"
              />
            </div>
            <div class="reminders-list-header-actions">
              <SecondaryButton label="Export to CSV" icon="bi bi-download" @click="exportCSV" />
              <SecondaryButton label="Search" icon="bi bi-search" @click="showSearchModal = true" />
            </div>
          </div>
        </div>

        <div class="reminders-list-glass-panel reminders-list-main-card">
          <div class="consultant-glass-table-section reminders-list-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table reminders-list-table">
                  <thead>
                    <tr>
                      <th class="reminders-list-col-select">
                        <input
                          type="checkbox"
                          class="reminders-list-header-checkbox"
                          disabled
                          aria-label="Select all (unavailable)"
                        />
                      </th>
                      <th>Name</th>
                      <th>Transaction Date</th>
                      <th>Transaction Type</th>
                      <th class="reminders-list-col-amount">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="displayRows.length === 0">
                      <td colspan="5" class="consultant-glass-table-no-records reminders-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(row, idx) in displayRows"
                        :key="row.id || idx"
                        class="consultant-glass-table-row reminders-list-row"
                        :style="{ '--rl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td class="reminders-list-col-select">
                          <input
                            type="checkbox"
                            class="reminders-list-row-checkbox"
                            :checked="isSelected(row.id)"
                            :aria-label="`Select ${row.name}`"
                            @change="toggleRowSelection(row.id, $event.target.checked)"
                          />
                        </td>
                        <td>{{ row.name }}</td>
                        <td>{{ row.transactionDate }}</td>
                        <td>{{ row.transactionType }}</td>
                        <td class="reminders-list-col-amount">{{ row.amount }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="reminders-list-card-footer">
            <div class="reminders-list-pagination" aria-label="Pagination">
              <span class="reminders-list-pagination-text">{{ paginationLabel }}</span>
              <div class="reminders-list-pagination-controls">
                <SecondaryButton
                  label=""
                  icon="bi bi-chevron-left"
                  aria-label="Previous page"
                  :disabled="!canPaginate"
                  @click="goPrevPage"
                />
                <SecondaryButton
                  label=""
                  icon="bi bi-chevron-right"
                  aria-label="Next page"
                  :disabled="!canPaginate"
                  @click="goNextPage"
                />
              </div>
            </div>
            <div class="reminders-list-bulk-actions">
              <PrimaryButton label="Create Selected" :disabled="selectedCount === 0" @click="onCreateSelected" />
              <SecondaryButton label="Skip Selected" :disabled="selectedCount === 0" @click="onSkipSelected" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <VendorDetailsSearch v-if="showSearchModal" @close="showSearchModal = false" />
  </div>
</template>

<script>
import '../styles/CompanyRemindersList.css';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'CompanyRemindersList',
  components: {
    VendorDetailsSearch,
    BrucdcumHeader,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      currentView: 'All',
      showSearchModal: false,
      reminders: [],
      selectedIds: [],
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    currentViewFilterOptions() {
      return [
        { value: 'All', label: 'All' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ];
    },
    filteredReminders() {
      if (this.currentView === 'All') return this.reminders;
      const wantActive = this.currentView === 'Active';
      return this.reminders.filter((row) => Boolean(row.active) === wantActive);
    },
    displayRows() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredReminders.slice(start, start + this.itemsPerPage);
    },
    totalFiltered() {
      return this.filteredReminders.length;
    },
    canPaginate() {
      return this.totalFiltered > 0;
    },
    paginationLabel() {
      if (this.totalFiltered === 0) return '0–0 of 0';
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.totalFiltered);
      return `${start}–${end} of ${this.totalFiltered}`;
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    selectedCount() {
      return this.selectedIds.length;
    },
  },
  watch: {
    currentView() {
      this.currentPage = 1;
      this.selectedIds = [];
    },
  },
  methods: {
    exportCSV() {
      if (this.$toast) {
        this.$toast.info('Export to CSV coming soon!');
      }
    },
    isSelected(id) {
      return this.selectedSet.has(id);
    },
    toggleRowSelection(id, checked) {
      if (checked) {
        if (!this.selectedSet.has(id)) this.selectedIds = [...this.selectedIds, id];
      } else {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },
    goPrevPage() {
      if (this.currentPage > 1) this.currentPage -= 1;
    },
    goNextPage() {
      const maxPage = Math.ceil(this.totalFiltered / this.itemsPerPage) || 1;
      if (this.currentPage < maxPage) this.currentPage += 1;
    },
    onCreateSelected() {
      if (this.$toast) {
        this.$toast.info('Create selected coming soon!');
      }
    },
    onSkipSelected() {
      if (this.$toast) {
        this.$toast.info('Skip selected coming soon!');
      }
    },
  },
};
</script>
